import React, { useContext, useEffect, useState } from "react";
import { BookOpenText, PenSquare, Trash2 } from "lucide-react";
import { AuthContext } from "../AuthContext";
import { API_BASE_URL } from "../api";
import { Button, Field, SectionHeader, SurfaceCard, TextareaField } from "../components/designSystem";

const initialForm = {
  id: "",
  title: "",
  content: "",
};

export default function Journal() {
  const { token } = useContext(AuthContext);
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadNotes() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_BASE_URL}/api/journal`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to load journal notes.");
        }

        if (active) {
          setNotes(Array.isArray(data) ? data : []);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message || "Failed to load journal notes.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    if (token) {
      loadNotes();
    }

    return () => {
      active = false;
    };
  }, [token]);

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const response = await fetch(
        form.id ? `${API_BASE_URL}/api/journal/${form.id}` : `${API_BASE_URL}/api/journal`,
        {
          method: form.id ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ title: form.title, content: form.content }),
        }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to save note.");
      }

      if (form.id) {
        setNotes((current) => current.map((note) => (note._id === data._id ? data : note)));
      } else {
        setNotes((current) => [data, ...current]);
      }

      setForm(initialForm);
    } catch (saveError) {
      setError(saveError.message || "Failed to save note.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this journal note?")) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/journal/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete note.");
      }

      setNotes((current) => current.filter((note) => note._id !== id));
      if (form.id === id) {
        setForm(initialForm);
      }
    } catch (deleteError) {
      setError(deleteError.message || "Failed to delete note.");
    }
  }

  function startEdit(note) {
    setForm({ id: note._id, title: note.title, content: note.content });
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_24%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_24%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Trading Journal"
          title="Document the thinking behind the executions"
          description="The ledger records outcomes. The journal captures context, patterns, and the internal logic that explains those outcomes."
        />
      </SurfaceCard>

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SurfaceCard>
          <SectionHeader
            eyebrow={form.id ? "Edit note" : "New note"}
            title={form.id ? "Refine this review entry" : "Write today’s market reflection"}
            description="Use this space for daily debriefs, setup reviews, mindset notes, or post-session analysis."
          />
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <Field
              label="Title"
              placeholder="London session review, discipline breakdown, FOMC prep"
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              required
            />
            <TextareaField
              label="Content"
              rows={12}
              placeholder="What did I see, what did I execute, what did I miss, what should change tomorrow?"
              value={form.content}
              onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
              required
            />
            <div className="flex flex-wrap gap-3">
              <Button type="submit" variant="primary" disabled={saving}>
                <PenSquare className="h-4 w-4" />
                {saving ? "Saving..." : form.id ? "Update Note" : "Save Note"}
              </Button>
              {form.id ? (
                <Button type="button" variant="secondary" onClick={() => setForm(initialForm)}>
                  Cancel edit
                </Button>
              ) : null}
            </div>
          </form>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader
            eyebrow="Saved notes"
            title="Your review archive"
            description="Keep journal notes visible and easy to revisit so insights do not disappear after one trading session."
          />
          <div className="mt-6 space-y-4">
            {loading ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading notes...</p>
            ) : notes.length ? (
              notes.map((note) => (
                <div key={note._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-950 dark:text-white">{note.title}</p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {new Date(note.updatedAt || note.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <BookOpenText className="h-5 w-5 text-slate-400" />
                  </div>
                  <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-600 dark:text-slate-300">{note.content}</p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <Button type="button" variant="brand" onClick={() => startEdit(note)}>
                      <PenSquare className="h-4 w-4" />
                      Edit
                    </Button>
                    <Button type="button" variant="danger" onClick={() => handleDelete(note._id)}>
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                No journal notes yet. Start capturing your session reviews and decision-making context here.
              </div>
            )}
          </div>
        </SurfaceCard>
      </div>
    </div>
  );
}